import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { Plus, X, Images, RotateCcw, Wand2, ArrowLeft, Loader2 } from "lucide-react";
import SaveDestinations from "@/components/gallery/SaveDestinations";

// Non-text COLLAGE builder. Purely client-side canvas composite (no AI, no
// prompt). Add 2–5 photos; the layout is chosen by how many you add and the
// orientation. Boxes are numbered and framed with thin black lines. "Generate"
// flattens everything into a single downloadable/savable collage image.

// Fractional box rects {x,y,w,h} in [0,1], in fill order (box 1 = index 0).
const LAYOUTS = {
  landscape: {
    // 1 big LEFT (solo) + 2 stacked right
    3: [{ x: 0, y: 0, w: 0.6, h: 1 }, { x: 0.6, y: 0, w: 0.4, h: 0.5 }, { x: 0.6, y: 0.5, w: 0.4, h: 0.5 }],
    // 1 big LEFT (solo) + 4 in a 2×2 on the right
    5: [{ x: 0, y: 0, w: 0.5, h: 1 },
        { x: 0.5, y: 0, w: 0.25, h: 0.5 }, { x: 0.75, y: 0, w: 0.25, h: 0.5 },
        { x: 0.5, y: 0.5, w: 0.25, h: 0.5 }, { x: 0.75, y: 0.5, w: 0.25, h: 0.5 }],
    // even 3×2 grid — equal sizing
    6: [{ x: 0, y: 0, w: 0.3334, h: 0.5 }, { x: 0.3333, y: 0, w: 0.3334, h: 0.5 }, { x: 0.6667, y: 0, w: 0.3333, h: 0.5 },
        { x: 0, y: 0.5, w: 0.3334, h: 0.5 }, { x: 0.3333, y: 0.5, w: 0.3334, h: 0.5 }, { x: 0.6667, y: 0.5, w: 0.3333, h: 0.5 }],
  },
  portrait: {
    // 1 big TOP (solo, full width) + 2 side-by-side bottom
    3: [{ x: 0, y: 0, w: 1, h: 0.55 }, { x: 0, y: 0.55, w: 0.5, h: 0.45 }, { x: 0.5, y: 0.55, w: 0.5, h: 0.45 }],
    // 1 big TOP (solo, full width) + 4 in a 2×2 below
    5: [{ x: 0, y: 0, w: 1, h: 0.5 },
        { x: 0, y: 0.5, w: 0.5, h: 0.25 }, { x: 0.5, y: 0.5, w: 0.5, h: 0.25 },
        { x: 0, y: 0.75, w: 0.5, h: 0.25 }, { x: 0.5, y: 0.75, w: 0.5, h: 0.25 }],
    // even 2×3 grid — equal sizing
    6: [{ x: 0, y: 0, w: 0.5, h: 0.3334 }, { x: 0.5, y: 0, w: 0.5, h: 0.3334 },
        { x: 0, y: 0.3333, w: 0.5, h: 0.3334 }, { x: 0.5, y: 0.3333, w: 0.5, h: 0.3334 },
        { x: 0, y: 0.6667, w: 0.5, h: 0.3333 }, { x: 0.5, y: 0.6667, w: 0.5, h: 0.3333 }],
  },
};

const loadImg = (src) => new Promise((res, rej) => {
  const im = new Image(); im.crossOrigin = "anonymous";
  im.onload = () => res(im); im.onerror = rej; im.src = src;
});

export default function CollageComposer({ ratio = "9:16", onExit }) {
  const [orientation, setOrientation] = useState(ratio === "16:9" ? "landscape" : "portrait");
  const [frame, setFrame] = useState("thin");           // thin | thick
  const [frameColor, setFrameColor] = useState("#000");  // "#000" | "#fff"
  const [count, setCount] = useState(3);     // template box count: 3 | 5 | 6
  const [imgs, setImgs] = useState([]);      // [{id, url}]  fill order = box order
  const [selBox, setSelBox] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resultUrl, setResultUrl] = useState(null);
  const [name, setName] = useState("my_collage");
  const fileRef = useRef(null);

  const boxes = LAYOUTS[orientation][count];

  useEffect(() => () => { if (resultUrl) URL.revokeObjectURL(resultUrl); }, [resultUrl]);

  // Switching template trims any extra photos beyond the new box count.
  const pickCount = (c) => { setCount(c); setImgs((prev) => prev.slice(0, c)); setSelBox(null); };

  const addFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setImgs((prev) => {
      const room = count - prev.length;
      if (room <= 0) { toast.warning(`This layout holds ${count} photos`); return prev; }
      const next = files.slice(0, room).map((f) => ({ id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, url: URL.createObjectURL(f) }));
      return [...prev, ...next];
    });
    e.target.value = "";
  };

  const removeAt = (i) => setImgs((prev) => prev.filter((_, idx) => idx !== i));

  // tap a box → then tap a thumb to place it there (swaps positions)
  const placeInBox = (thumbIdx) => {
    if (selBox == null) return;
    setImgs((prev) => {
      if (selBox >= prev.length || thumbIdx >= prev.length) return prev;
      const next = [...prev];
      [next[selBox], next[thumbIdx]] = [next[thumbIdx], next[selBox]];
      return next;
    });
    setSelBox(null);
  };

  const reset = () => { setImgs([]); setSelBox(null); if (resultUrl) URL.revokeObjectURL(resultUrl); setResultUrl(null); };

  const generate = async () => {
    if (imgs.length < count) { toast.error(`Add ${count - imgs.length} more photo${count - imgs.length > 1 ? "s" : ""} to fill the layout`); return; }
    setBusy(true);
    try {
      const W = orientation === "landscape" ? 1920 : 1080;
      const H = orientation === "landscape" ? 1080 : 1920;
      const gap = Math.round(W * (frame === "thick" ? 0.022 : 0.006));   // frame gutter
      const line = Math.max(2, W * (frame === "thick" ? 0.006 : 0.0015));
      const canvas = document.createElement("canvas");
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = frameColor; ctx.fillRect(0, 0, W, H);
      const loaded = await Promise.all(imgs.slice(0, count).map((it) => loadImg(it.url)));
      boxes.forEach((b, i) => {
        const im = loaded[i]; if (!im) return;
        const bx = b.x * W + gap / 2, by = b.y * H + gap / 2;
        const bw = b.w * W - gap, bh = b.h * H - gap;
        // cover-fit crop
        const s = Math.max(bw / im.naturalWidth, bh / im.naturalHeight);
        const sw = bw / s, sh = bh / s;
        const sx = (im.naturalWidth - sw) / 2, sy = (im.naturalHeight - sh) / 2;
        ctx.save();
        ctx.beginPath(); ctx.rect(bx, by, bw, bh); ctx.clip();
        ctx.drawImage(im, sx, sy, sw, sh, bx, by, bw, bh);
        ctx.restore();
        ctx.strokeStyle = frameColor; ctx.lineWidth = line;
        ctx.strokeRect(bx, by, bw, bh);
      });
      const blob = await new Promise((r) => canvas.toBlob(r, "image/png", 0.95));
      if (resultUrl) URL.revokeObjectURL(resultUrl);
      setResultUrl(URL.createObjectURL(blob));
    } catch (e) { toast.error("Could not build the collage"); }
    finally { setBusy(false); }
  };

  const aspectClass = orientation === "landscape" ? "aspect-[16/9]" : "aspect-[9/16]";

  // ── Result view ──────────────────────────────────────────────────────
  if (resultUrl) {
    return (
      <div className="h-full w-full flex flex-col min-h-0 bg-slate-950 text-slate-100" data-testid="collage-result">
        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pt-4 pb-4 space-y-4">
          <p className="font-mono text-[11px] font-black uppercase tracking-widest text-cyan-300">Collage ready</p>
          <div className={`w-full ${aspectClass} max-h-[62vh] mx-auto bg-black border-2 border-black flex items-center justify-center overflow-hidden`}>
            <img src={resultUrl} alt="collage" className="w-full h-full object-contain" data-testid="collage-result-image" />
          </div>
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="collage-name"
            className="w-full bg-slate-900 border-2 border-slate-700 focus:border-cyan-500 outline-none px-3 py-2.5 text-sm text-white font-serif rounded-lg" />
          <SaveDestinations url={resultUrl} track="IMAGES" filename={name || "my_collage"} ext="png" />
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => { if (resultUrl) URL.revokeObjectURL(resultUrl); setResultUrl(null); }} data-testid="collage-back-edit"
              className="flex items-center justify-center gap-2 border-2 border-slate-700 bg-slate-900 text-slate-200 py-2.5 rounded-lg font-mono text-[10px] font-black uppercase tracking-widest">
              <ArrowLeft size={14} /> Edit
            </button>
            <button onClick={reset} data-testid="collage-result-reset"
              className="flex items-center justify-center gap-2 border-2 border-slate-700 bg-slate-900 text-slate-300 py-2.5 rounded-lg font-mono text-[10px] font-black uppercase tracking-widest">
              <RotateCcw size={14} /> Start Over
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Builder view ─────────────────────────────────────────────────────
  return (
    <div className="h-full w-full flex flex-col min-h-0 bg-slate-950 text-slate-100" data-testid="collage-composer">
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pt-4 pb-4 space-y-4">
        <div className="flex items-center justify-between">
          <p className="font-mono text-[11px] font-black uppercase tracking-widest text-cyan-300 flex items-center gap-2"><Images size={14} /> Collage</p>
          {onExit && <button onClick={onExit} data-testid="collage-exit" className="text-[11px] font-mono text-slate-400 hover:text-white">✕ Close</button>}
        </div>

        {/* Orientation */}
        <div className="grid grid-cols-2 gap-2">
          {[["portrait", "Portrait 9:16"], ["landscape", "Landscape 16:9"]].map(([k, lbl]) => (
            <button key={k} onClick={() => setOrientation(k)} data-testid={`collage-orientation-${k}`}
              className={`py-2.5 rounded-lg border-2 font-mono text-[10px] font-black uppercase tracking-widest transition-colors ${orientation === k ? "border-cyan-400 bg-cyan-400/15 text-cyan-200" : "border-slate-700 bg-slate-900 text-slate-400"}`}>
              {lbl}
            </button>
          ))}
        </div>

        {/* Layout — number of photo boxes */}
        <div className="grid grid-cols-3 gap-2" data-testid="collage-layout-picker">
          {[[3, "3 · Feature"], [5, "5 · Feature"], [6, "6 · Even"]].map(([c, lbl]) => (
            <button key={c} onClick={() => pickCount(c)} data-testid={`collage-count-${c}`}
              className={`py-2 rounded-lg border-2 font-mono text-[9px] font-black uppercase tracking-widest transition-colors ${count === c ? "border-cyan-400 bg-cyan-400/15 text-cyan-200" : "border-slate-700 bg-slate-900 text-slate-400"}`}>
              {lbl}
            </button>
          ))}
        </div>

        {/* Frame style */}
        <div className="flex items-center gap-2">
          <div className="grid grid-cols-2 gap-2 flex-1">
            {[["thin", "Thin"], ["thick", "Thick"]].map(([k, lbl]) => (
              <button key={k} onClick={() => setFrame(k)} data-testid={`collage-frame-${k}`}
                className={`py-2 rounded-lg border-2 font-mono text-[9px] font-black uppercase tracking-widest transition-colors ${frame === k ? "border-cyan-400 bg-cyan-400/15 text-cyan-200" : "border-slate-700 bg-slate-900 text-slate-400"}`}>
                {lbl} Frame
              </button>
            ))}
          </div>
          {[["#000", "Black"], ["#fff", "White"]].map(([c, lbl]) => (
            <button key={c} onClick={() => setFrameColor(c)} data-testid={`collage-framecolor-${lbl.toLowerCase()}`}
              title={`${lbl} frame`}
              className={`w-9 h-9 rounded-lg border-2 ${frameColor === c ? "border-cyan-400 ring-2 ring-cyan-400/40" : "border-slate-600"}`}
              style={{ background: c }} />
          ))}
        </div>

        {/* Live preview with numbered boxes */}
        <div className={`relative w-full ${aspectClass} max-h-[46vh] mx-auto rounded-md overflow-hidden`}
          style={{ background: frameColor, padding: frame === "thick" ? "6px" : "2px" }} data-testid="collage-preview">
          {boxes.map((b, i) => {
            const img = imgs[i];
            const filled = !!img;
            const bw = frame === "thick" ? 4 : 1.5;
            return (
              <button key={i} data-testid={`collage-box-${i}`}
                onClick={() => (filled ? setSelBox(selBox === i ? null : i) : fileRef.current?.click())}
                style={{ position: "absolute", left: `${b.x * 100}%`, top: `${b.y * 100}%`, width: `${b.w * 100}%`, height: `${b.h * 100}%`, border: `${bw}px solid ${frameColor}`, boxSizing: "border-box" }}
                className={`overflow-hidden ${selBox === i ? "ring-2 ring-cyan-400 z-10" : ""}`}>
                {filled
                  ? <img src={img.url} alt="" className="w-full h-full object-cover" />
                  : <span className="w-full h-full flex items-center justify-center bg-slate-800/60 text-slate-500"><Plus size={20} /></span>}
                <span className="absolute top-1 left-1 w-5 h-5 rounded-full bg-black/70 text-white text-[10px] font-black flex items-center justify-center">{i + 1}</span>
              </button>
            );
          })}
        </div>
        <p className="text-center font-mono text-[9px] text-slate-500">
          {selBox != null ? `Box ${selBox + 1} selected — tap a photo below to move it here` : `${imgs.length}/${count} photos · box 1 is the feature`}
        </p>

        {/* Thumbnails */}
        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={addFiles} data-testid="collage-file-input" />
        <div className="flex flex-wrap gap-2">
          {imgs.map((it, i) => (
            <div key={it.id} className="relative" data-testid={`collage-thumb-${i}`}>
              <button onClick={() => placeInBox(i)} className={`w-14 h-14 rounded-lg overflow-hidden border-2 ${selBox != null ? "border-cyan-400" : "border-slate-700"}`}>
                <img src={it.url} alt="" className="w-full h-full object-cover" />
              </button>
              <span className="absolute -top-1 -left-1 w-4 h-4 rounded-full bg-cyan-500 text-black text-[9px] font-black flex items-center justify-center">{i + 1}</span>
              <button onClick={() => removeAt(i)} data-testid={`collage-remove-${i}`}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center"><X size={11} /></button>
            </div>
          ))}
          {imgs.length < count && (
            <button onClick={() => fileRef.current?.click()} data-testid="collage-add-image"
              className="w-14 h-14 rounded-lg border-2 border-dashed border-slate-600 text-slate-400 flex items-center justify-center active:scale-95">
              <Plus size={18} />
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2 pt-1">
          <button onClick={reset} data-testid="collage-reset"
            className="flex items-center justify-center gap-2 border-2 border-slate-700 bg-slate-900 text-slate-300 py-3 rounded-lg font-mono text-[10px] font-black uppercase tracking-widest">
            <RotateCcw size={13} /> Reset
          </button>
          <button onClick={generate} disabled={busy || imgs.length < count} data-testid="collage-generate"
            className="flex items-center justify-center gap-2 border-2 border-black bg-cyan-400 text-black py-3 rounded-lg font-mono text-[10px] font-black uppercase tracking-widest shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} Generate
          </button>
        </div>
      </div>
    </div>
  );
}
